// SheetDB-kalkylark för alla Halloweek-bokningar.
// Varje event har en egen flik (sheet) med samma kolumnnamn utan prefix.
const EVENT_SHEETDB_URL = 'https://sheetdb.io/api/v1/mg76ebnkl4cfn'

interface EventSheetConfig {
  /** Exact tab name in the spreadsheet */
  tab: string
  code: string
  /** Column that stores the booking choice */
  choiceColumn?: string
  hasPartySize?: boolean
}

const EVENT_SHEET: Record<string, EventSheetConfig> = {
  'halloween-run-club': { tab: 'Run Club', code: 'RUN', choiceColumn: 'Deltar i' },
  'halloween-hidden-reality': {
    tab: 'Mordmysterier',
    code: 'MORD',
    choiceColumn: 'Spelform',
    hasPartySize: true,
  },
  'halloween-barnens-dag': { tab: 'Lilla Monster Loppet', code: 'LOPP' },
  'halloween-monsterdisko': { tab: 'Monsterdisko', code: 'DISKO' },
  'halloween-hostbrunch': {
    tab: 'Brunch',
    code: 'BRUNCH',
    hasPartySize: true,
  },
}

/**
 * Booked (confirmed) people per time, read from the event's tab.
 * Returns null if the sheet can't be read.
 */
export async function fetchSlotCounts(
  eventId: string,
): Promise<Record<string, number> | null> {
  const cfg = EVENT_SHEET[eventId]
  if (!cfg) return null
  try {
    const res = await fetch(
      `${EVENT_SHEETDB_URL}?sheet=${encodeURIComponent(cfg.tab)}`,
      { headers: { Accept: 'application/json' } },
    )
    if (!res.ok) return null
    const rows: Record<string, string>[] = await res.json()
    const counts: Record<string, number> = {}
    for (const row of rows) {
      if ((row.Status ?? '').trim().toLowerCase() === 'väntelista') continue
      const time = (row.Tid ?? '').trim()
      const people = Number(row['Antal personer']) || 0
      if (time) counts[time] = (counts[time] ?? 0) + people
    }
    return counts
  } catch {
    return null
  }
}

export interface BookingStats {
  /** Total bookings (rows) in the tab */
  total: number
  /** Bookings and people per choice (e.g. "Öppet spel") */
  byChoice: Record<string, { bookings: number; people: number }>
}

/**
 * Bookings in the event's tab, excluding waitlisted ones – in total and per
 * choice. Returns null if the sheet can't be read.
 */
export async function fetchBookingStats(
  eventId: string,
): Promise<BookingStats | null> {
  const cfg = EVENT_SHEET[eventId]
  if (!cfg) return null
  try {
    const res = await fetch(
      `${EVENT_SHEETDB_URL}?sheet=${encodeURIComponent(cfg.tab)}&t=${Date.now()}`,
      { headers: { Accept: 'application/json' } },
    )
    if (!res.ok) return null
    const rows: Record<string, string>[] = await res.json()
    const stats: BookingStats = { total: 0, byChoice: {} }
    for (const row of rows) {
      if ((row.Bokningsnummer ?? '').trim() === '') continue
      if ((row.Status ?? '').trim().toLowerCase() === 'väntelista') continue
      stats.total += 1
      const choice = cfg.choiceColumn ? (row[cfg.choiceColumn] ?? '').trim() : ''
      if (!choice) continue
      const entry = stats.byChoice[choice] ?? { bookings: 0, people: 0 }
      entry.bookings += 1
      entry.people += Number(row['Antal personer']) || 0
      stats.byChoice[choice] = entry
    }
    return stats
  } catch {
    return null
  }
}

export function createBookingNumber(eventId: string): string {
  const code = EVENT_SHEET[eventId]?.code ?? 'EV'
  return `HW-${code}-${Math.floor(1000 + Math.random() * 9000)}`
}

interface EventBooking {
  eventId: string
  bookingNumber: string
  date: string
  time: string
  choice: string | null
  partySize: number | null
  /** "Förälder" or "Barnet själv" – only for children's events */
  registeredBy?: string
  /** Number of children – only for children's events */
  childCount?: number
  /** "Bekräftad" / "Väntelista" – only for events with time slots */
  status?: string
  firstName: string
  lastName: string
  email: string
  phone: string
}

export async function saveEventBooking(b: EventBooking): Promise<boolean> {
  const cfg = EVENT_SHEET[b.eventId]
  if (!cfg) return false
  const row: Record<string, string> = {
    Bokningsnummer: b.bookingNumber,
    Skickad: new Date().toLocaleString('sv-SE', {
      timeZone: 'Europe/Stockholm',
    }),
    Datum: b.date,
    Tid: b.time,
    Förnamn: b.firstName,
    Efternamn: b.lastName,
    'E-post': b.email,
    Telefon: b.phone,
  }
  if (b.registeredBy) row['Anmäld av'] = b.registeredBy
  if (b.childCount !== undefined) row['Antal barn'] = String(b.childCount)
  if (b.status) row.Status = b.status
  if (cfg.choiceColumn) row[cfg.choiceColumn] = b.choice ?? ''
  if (cfg.hasPartySize)
    row['Antal personer'] = b.partySize ? String(b.partySize) : ''

  try {
    const res = await fetch(
      `${EVENT_SHEETDB_URL}?sheet=${encodeURIComponent(cfg.tab)}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ data: [row] }),
      },
    )
    return res.ok
  } catch {
    return false
  }
}
